/**
 * 节点目录 —— **内核侧的装配点**，也是**节点包的安装点**。
 *
 * ⚠️ 这个文件以前是 700 行的节点目录本体，长在内核里。
 * 现在它只是「把启用的包合成一个注册表，再把注册表的接口转发出去」。
 * 调用方（菜单、拖线、序列化）的用法**完全没变**：
 * `NODE_TYPES` / `nodeTypeOf` / `makeModelOfType` 还是那几个名字。
 *
 * ## 运行时装/卸包（这一步才让「可选加载」成立）
 *
 * 包的来源有**三类**：
 *   · **内核包** —— 强制加载，不可卸载；
 *   · **数据包**（`package.json`）—— 带 expr 源码，内置两个 + 用户导入的；
 *   · **代码包**（TS 模块）—— 带 TS 求值器，**能贡献编辑器**（`editors`）。
 *     ⚠️ 主程序里**今天没有内置代码包**（`BUILT_IN_CODE_PACKAGES` 是空的）；
 *     这类包经 `setPackages({ extra })` 进注册表 —— 验证套件装的那个夹具
 *     （`scripts/fixtures/editor-package.ts`）走的就是这条路。
 *
 * ⚠️ 装卸是**运行时**发生的，所以这里有两条硬要求：
 *
 * ① **导出的容器必须原地更新**。`NODE_TYPES` 被好几个模块
 *    `import` 走了（菜单、拖线、宿主），**换一个数组对象**它们看不到。
 *    所以是 `length = 0` + `push(...)`，而不是重新赋值。
 *    （`BUILTIN_EVALUATORS` 同理，逐键改。**编辑器注册表也一样** ——
 *    见 `layout/editorRegistry.ts` 的文件头。）
 *
 * ② **必须有个版本号**。宿主那边算求值结果是 `computed`，
 *    读的是一个普通对象 —— 原地改它**不会触发重算**。
 *    所以装/卸之后 `packagesVersion` 自增，宿主把它读进依赖里。
 *
 * ## 这个文件还是**唯一的装配点**
 *
 * 「内核代码里只有这里可以 import 一个包」由 `verify:boundary` 守着。
 * 编辑器贡献也走这一条：包能贡献窗口类型，但**内核不许引用任何
 * 包贡献的编辑器**（`verify:layers` 那条静态检查）——
 * 装配点负责把两者接上，接法就在下面的 `rebuild()` 里。
 *
 * ## 缺包会怎样
 *
 * 文档里逐节点存了 `rows`（`graph/serialization.ts`），所以包没装时
 * **节点照样画得出来**，只是它的求值器不在表里 —— 求值层会报一条
 * 「未注册求值器」的诊断（见 `evaluate.ts` 的注释）。
 * 也就是说：**缺包不影响打开文档**，只影响那部分能不能算。
 *
 * ⚠️ 编辑器不一样：窗口没有主体就画不出来，所以卸载一个贡献编辑器的包
 * 之后，那些窗口会被**回落到节点图**（`areas.ts` 的 `settleEditors`，
 * 由宿主在包变化时调用）。两种反应都遵守同一条原则 ——
 * **不静默删用户的东西**：节点留着，窗口也留着。
 */
import { ref } from 'vue'
import { CONST_NODE_TYPES } from '../node/constNodes'
import { CORE_NODE_TYPES } from '../node/coreNodes'
import { CORE_EVALUATORS } from '../node/evaluators'
import { jsRunner } from '../node/jsNodeType'
import type { NodeEvaluator } from '../node/evaluate'
import type { NodeRunner } from '../node/runGraph'
import {
  createNodeRegistry,
  type NodeCategoryDef,
  type NodePackage,
  type NodeTypeDef,
  type PackageExample,
} from '../node/package'
import { registerEditorContributions } from '../layout/editorRegistry'
import DATA_PACKAGE_FILE from '../domains/data/package.json'
import FUNCTION_PACKAGE_FILE from '../domains/function/package.json'
/* ⚠️ 打印包是**内置代码包**：它有 TS 求值器和编辑器组件，JSON 里放不下 */
import { PRINT_PACKAGE } from '../domains/print'
/* ⚠️ 事件包也是内置代码包：它带 **runners**（副作用执行器），JSON 里放不下 */
import { EVENT_PACKAGE } from '../domains/event'
/* ⚠️ 工具包是**第一个真干活的域包**：它带 `net` 能力声明与抓取执行器 */
import { TOOL_PACKAGE } from '../domains/tool'
import type { GroupAssetDef } from '../node/asset'
import { parsePackageFile, type PackageFile } from '../node/packageFile'

/**
 * **内核包**。
 *
 * ⚠️ 内核也是一个包 —— 这样「启用哪些包」就只有一种写法，
 * 不需要给内核开特例（特例就是下一次边界侵蚀的入口）。
 * 它和别的包唯一的区别是：**它强制加载，不可卸载**。
 */
const CORE_PACKAGE: NodePackage = {
  id: 'core',
  label: '内核',
  kind: 'core',
  /*
   * ⚠️ 常量族和结构构造分两个文件、合成一个包：
   * 「常量」是基础计算的**输入**，「工具」是图自己的构造 —— 都是内核能力，
   * 不是可选加载的领域能力，所以它们不拆成两个包。
   */
  categories: [
    { id: 'value', label: '常量' },
    { id: 'utility', label: '工具' },
  ],
  nodeTypes: [...CONST_NODE_TYPES, ...CORE_NODE_TYPES],
  evaluators: CORE_EVALUATORS,
  /*
   * ★ 内核也能带**副作用执行器**（和域包同一条通道）——
   * JS 节点是内核提供的通用逻辑节点，它的执行器当然跟着内核走。
   */
  runners: { 'core.js': jsRunner },
}

/**
 * 随程序一起发的**内置包**（数据包那一半）—— 默认装上。
 *
 * ⚠️ 内置包也走 `parsePackageFile` 同一道校验。
 * 好处是「内核之外的一切包都得过校验」这条规矩没有例外 ——
 * 有例外的那种包，迟早会成为坏数据的漏口。
 *
 * ⚠️ 但内置包**多一项权利**：它可以声明 `kind: 'base'`
 * （核心基础能力，永远启用、不可卸载）。用户导入的文件不行 ——
 * 那是 `parsePackageFile({ builtIn: true })` 这个开关的**唯一**用途。
 */
const BUILT_IN_PACKAGE_FILES: readonly PackageFile[] = (() => {
  const raw: [string, unknown][] = [
    ['function', FUNCTION_PACKAGE_FILE],
    ['data', DATA_PACKAGE_FILE],
  ]
  return raw.map(([name, json]) => {
    const parsed = parsePackageFile(json, { builtIn: true })
    if (!parsed.ok) {
      /*
       * ⚠️ 内置包校验不过 = 我们自己发错了版本。**大声报出来**，
       * 而不是默默少几个节点 —— 后者会让人以为是加载机制坏了。
       */
      throw new Error(
        `内置节点包「${name}」没通过校验（这是程序自己的问题）：\n${parsed.problems.join('\n')}`,
      )
    }
    return parsed.file
  })
})()

/**
 * 随程序一起发的**内置代码包** —— 今天**有一个**：打印包。
 *
 * ⚠️ 「代码包」（TS 模块，能带 TS 求值器和**编辑器组件**）和「数据包」
 * （JSON，能带 expr 源码）是两条路：
 *
 * | | 数据包（`BUILT_IN_PACKAGE_FILES`） | 代码包（这里） |
 * |---|---|---|
 * | 内容 | JSON（expr 源码、节点行） | TS 模块（TS 求值器 + **编辑器组件**） |
 * | 能贡献 UI | ❌（`parsePackageFile` 明确拒绝） | ✅ |
 * | 能导出成文件 | ✅ | ❌（`JSON.stringify` 会静默丢掉组件） |
 *
 * ⚠️ 两者在装配上**完全同级**：都默认装上、都由 `disabled` 关掉
 * （只要它是 `domain`）、都进「视图 → 节点包」那一栏清单。
 *
 * ⚠️ 第一个真货是 `print`（打印包）：节点 + 求值器 + 页面编辑器
 * （`print.layout`）。它是 `kind: 'domain'`，所以**可以卸载** ——
 * 卸载之后节点照常显示、窗口回落到默认编辑器，装回来窗口自己就回来。
 */
const BUILT_IN_CODE_PACKAGES: readonly NodePackage[] = [PRINT_PACKAGE, EVENT_PACKAGE, TOOL_PACKAGE]

/** 菜单分组顺序 —— **呈现决策**，和包顺序无关 */
const CATEGORY_ORDER = [
  /*
   * ⚠️ 打印域排**最前**：一份打印模板是从「一页纸」开始的 ——
   * 先建页面，再往里放组件，数据源是后话。
   * 数据源那一档的注释（「搭模板是从它开始的」）说的是**通用**模板，
   * 而今天的主场景就是打印，所以域节点优先。
   */
  'print',
  'data',
  /*
   * ⚠️ 「事件」排在**数据源之后、常量之前**：搭一份工具流是从
   * "什么让它跑起来"开始的（用户原话："通过一些事件……当事件被编辑器
   * 触发的时候节点就会开始运行"），但打印域仍然是主场景，排最前。
   */
  'event',
  /*
   * ⚠️「工具」排在事件之后、常量之前：一份工具流的读法是
   * "什么触发它 → 它去干什么 → 算什么"，而打印域仍然排最前（今天的主场景）。
   */
  'tool',
  'value',
  'logic',
  'boolean',
  'text',
  'number',
  'color',
  'time',
  'list',
  'convert',
  'utility',
]

const registryOptions = { categoryOrder: CATEGORY_ORDER }

/* ══ 原地更新的容器（见文件头 ①） ══════════════════════════════ */

const nodeTypes: NodeTypeDef[] = []
const categories: NodeCategoryDef[] = []
const codeProblems: { type: string; problems: { message: string; at: number }[] }[] = []
const evaluators: Record<string, NodeEvaluator> = {}
/** 副作用执行器（原地更新，理由同 `nodeTypes`） */
const runners: Record<string, NodeRunner> = {}
/** 全部包合起来的资产（原地更新，理由同 `nodeTypes`） */
const assets: GroupAssetDef[] = []
/**
 * 全部包合起来的**示例文档**（原地更新，理由同 `nodeTypes`）。
 *
 * ★ 用户要的那条：「域节点包可以带示例文档，被自动加载到**文件菜单下的
 * 示例文件菜单下**」—— 于是这份清单只服务一个菜单，不需要别的地方读它。
 */
const examples: PackageExample[] = []
const assetProblems: { asset: string; problems: string[] }[] = []

/** 按标识查节点类型 —— 用 Map，装/卸时重建 */
let byType = new Map<string, NodeTypeDef>()
let byCategory = new Map<string, string>()
/** 分类 id → 图标（回退用，见 `nodeIconOf`） */
let byCategoryIcon = new Map<string, string>()
/** 节点类型 → 提供它的包 id（写进文档的 `pkg` 用；装/卸时重建） */
let ownerOf = new Map<string, string>()

/**
 * 包变化后的版本号。
 *
 * ⚠️ 宿主**必须**把它读进依赖里：`BUILTIN_EVALUATORS` 是普通对象，
 * 原地改它不会触发任何 `computed` 重算 —— 卸载一个包之后
 * 求值结果不会更新，表现是「卸了但还在算」。
 */
export const packagesVersion = ref(0)

/** 当前装着的包（内核 + 数据包），顺序 = 菜单分组顺序 */
export const ACTIVE_PACKAGES: NodePackage[] = []

function rebuild(dataPackages: readonly NodePackage[]): void {
  ACTIVE_PACKAGES.length = 0
  ACTIVE_PACKAGES.push(CORE_PACKAGE, ...dataPackages)

  const registry = createNodeRegistry(ACTIVE_PACKAGES, registryOptions)

  /*
   * ★ 编辑器注册表**跟着包一起重算**（同一批包，同一个装配点）。
   *
   * ⚠️ 顺序无所谓（两边互不依赖），但**必须一起做**：包能贡献编辑器，
   * 所以「装了什么包」和「有哪些窗口」是同一个事实的两面。
   * 卸载一个贡献编辑器的包之后，这里会用更少的贡献重来一遍，
   * 窗口类型菜单自然回到内核那几条。
   */
  registerEditorContributions(
    ACTIVE_PACKAGES.map((pack) => ({ id: pack.id, editors: pack.editors ?? [] })),
  )

  nodeTypes.length = 0
  nodeTypes.push(...registry.nodeTypes)
  categories.length = 0
  categories.push(...registry.categories)
  codeProblems.length = 0
  codeProblems.push(...registry.codeProblems)

  /* ⚠️ 求值器表也要原地改：清掉旧的键，再铺新的 */
  for (const key of Object.keys(evaluators)) delete evaluators[key]
  Object.assign(evaluators, registry.evaluators)
  /* 执行器同理（装卸包时一起换，见 `BUILTIN_RUNNERS` 的说明） */
  for (const key of Object.keys(runners)) delete runners[key]
  Object.assign(runners, registry.runners)

  assets.length = 0
  assets.push(...registry.assets)
  examples.length = 0
  examples.push(...registry.examples)
  assetProblems.length = 0
  assetProblems.push(...registry.assetProblems)

  byType = new Map(nodeTypes.map((def) => [def.type, def]))
  byCategory = new Map(categories.map((c) => [c.id, c.label]))
  byCategoryIcon = new Map(
    categories.filter((c) => c.icon).map((c) => [c.id, c.icon as string]),
  )
  /*
   * ⚠️ 「谁提供的」**从注册表问**（`registry.packageOf`），而不是在这里
   * 自己按包顺序摊一遍 —— 重复注册是注册表抛的错，来源也该由它说了算
   * （两份判断迟早不一致）。
   */
  ownerOf = new Map(
    nodeTypes
      .map((def) => [def.type, registry.packageOf(def.type)] as const)
      .filter((entry): entry is [string, string] => Boolean(entry[1])),
  )

  packagesVersion.value += 1
}

/* ══ 安装 / 卸载 ═══════════════════════════════════════════════ */

/**
 * 设定「装哪些包」。
 *
 * ⚠️ **内置包由这里负责拼进来** —— 调用方只说自己**额外**装了什么、
 * 关掉了哪些，不负责记得内置包（代码包和数据包都不必记得）。
 *
 * 第一版是 `installDataPackages(数据包数组)`，让调用方自己保证
 * 「数组里得有内置包」—— 测试里立刻就踩了：传一个 `[新包]` 上去，
 * 函数包**无声地消失了**（节点数从 21 变成 17）。这种「必须记得带上某些东西」
 * 的 API 迟早会被用错，所以把组装收进函数内部。
 *
 * ⚠️ `extra` 收的是 `NodePackage` 而**不是** `PackageFile`：所以它同样能装
 * **代码包**（带 TS 求值器和编辑器组件的那种）—— 验证套件就是这么装夹具的
 * （`scripts/fixtures/editor-package.ts`）。用户从界面导入的只能是数据包
 * （JSON 放不下函数和组件），但宿主这一侧没有这个限制。
 */
export function setPackages(state: {
  /** 额外装上的包（用户导入的数据包，或宿主/验证装进来的代码包） */
  extra?: readonly NodePackage[]
  /**
   * 被关掉的内置包 id。
   *
   * ⚠️ **只对 `domain` 生效**：`base`（核心基础能力，函数节点那种）和
   * `core`（内核）**没有"关掉"这个状态** —— 用户能把编辑器的基本功卸掉，
   * 只会得到一堆算不出值的节点（见 `node/package.ts` 的 `kind` 表）。
   * 拿 base 的 id 调这里**什么也不会发生**（不是抛错：调用方可能只是
   * 拿一份陈旧的清单来重放，那种情况不该把界面搞崩）。
   */
  disabled?: readonly string[]
} = {}): void {
  const disabled = new Set(state.disabled ?? [])
  const enabled = builtInPackages().filter((pack) => pack.kind !== 'domain' || !disabled.has(pack.id))
  rebuild([...enabled, ...(state.extra ?? [])])
}

/** 装了哪些**可选**包（`domain`，不含内核与基础包） */
export function dataPackages(): readonly NodePackage[] {
  return ACTIVE_PACKAGES.filter((p) => p.kind === 'domain')
}

/**
 * 随程序发的内置包**文件**（数据包那一半；`base` + `domain` 都在里面）。
 *
 * ⚠️ 它只是**内置包的一半**（另一半是代码包，见 `builtInPackages()`）。
 * 想知道"这个包是不是内置的"，用 `builtInPackages()` —— 拿这一个当判据
 * 会漏掉打印包那类代码包（它没有文件形态）。
 */
export function builtInPackageFiles(): readonly PackageFile[] {
  return BUILT_IN_PACKAGE_FILES
}

/**
 * 随程序一起发的**全部内置包**（代码包 + 数据包）—— 「是不是内置」的**单一来源**。
 *
 * ## 为什么必须只有一个来源（这是踩出来的）
 *
 * 打印包变成内置**代码包**之后，「内置」这件事就有了两种形态：
 * 有文件的数据包（`builtInPackageFiles()`）和只有代码的包（`BUILT_IN_CODE_PACKAGES`）。
 * 而当时有三处判断只认前者：
 *
 * | 位置 | 后果 |
 * |---|---|
 * | `usePackages.installed`（节点包清单） | **打印包根本不出现在菜单里** —— 看不到、也卸不掉 |
 * | `usePackages.install`（导入重名检查） | 用户能导入一个 id 叫 `print` 的包 → 节点类型重复 → 注册表抛错、界面崩 |
 * | `verify:render` 的 extra 过滤 | 把内置包又当 extra 装了一遍 → 当场抛「节点类型标识重复」 |
 *
 * 三处同一个根因：**判据有第二份，而它只覆盖了一半**。
 * 所以「是不是内置」现在只由这个函数回答，调用方不许自己拼。
 */
export function builtInPackages(): readonly NodePackage[] {
  return [...BUILT_IN_CODE_PACKAGES, ...BUILT_IN_PACKAGE_FILES.map((file) => file.package)]
}

/* 首次装配：内核 + 内置代码包 + 内置数据包（和 `setPackages({})` 同一条路） */
setPackages()

/** 内置包里的那些数据包（`setPackages` 内部用；导出是为了让宿主能列出它们） */
export { BUILT_IN_PACKAGE_FILES }

/* ══ 对外接口（名字不变，调用方零改动） ═══════════════════════ */

/** 全部包合起来的节点目录 */
export const NODE_TYPES: NodeTypeDef[] = nodeTypes

/** 全部包合起来的分类 */
export const NODE_CATEGORIES = categories

/**
 * 源码编译不过的节点类型（数据包的 `code` 有问题时）。
 *
 * 正常应当是空的 —— 编译不过**不会**让节点消失，只是求值时在它自己身上
 * 报一条具体诊断。宿主可以拿这个列表做「包校验」提示。
 */
export const PACKAGE_CODE_PROBLEMS = codeProblems

/**
 * **包贡献的编辑器**那一侧的问题清单（正常应当是空的）。
 *
 * ⚠️ 和 `PACKAGE_CODE_PROBLEMS` / `ASSET_PROBLEMS` 并列 —— 三者都是
 * 「校验期查出来的、不该留给运行时的问题」，宿主可以一起拿去做「包校验」提示。
 *
 * ⚠️ 它有问题的编辑器**照样在窗口类型菜单里**（灰着，标「未实现」），
 * 不是整条消失 —— 见 `editorRegistry.ts` 的两种错误两种反应。
 */
export { EDITOR_PROBLEMS } from '../layout/editorRegistry'

/** 按标识查节点类型 */
export function nodeTypeOf(type: string): NodeTypeDef | undefined {
  return byType.get(type)
}

/** 分类标识 → 显示名（未知标识原样返回） */
export function categoryLabel(id: string): string {
  return byCategory.get(id) ?? id
}

/**
 * ★ **这个类型在节点头部画什么图标**（2027 加）。
 *
 * 两级：类型自己的 `icon` → 分类的 `icon` → 都没有就是 `undefined`
 * （header 会回落成那个「!」）。
 *
 * ⚠️ 抽成一个函数而不是在两处各写一遍：`verify:packages` 要遍历注册表
 * 检查"每个类型的图标都取得到"，而 header 要的是同一个答案。
 */
export function nodeIconOf(type: string): string | undefined {
  const def = byType.get(type)
  if (!def) return undefined
  return def.icon ?? byCategoryIcon.get(def.category)
}

/**
 * 节点类型 → **提供它的包 id**（没装/没登记就是 `undefined`）。
 *
 * ⚠️ 宿主把它注入给序列化层（`toDocument({ packageOf })`），文档里于是
 * 逐节点记着 `pkg` —— 「这份模板依赖哪些包」从此不必先有注册表才能回答。
 * 装配点必须**现查**（和 `nodeTypeOf` 同一个理由：装卸包之后表是新的）。
 */
export function nodePackageOf(type: string): string | undefined {
  return ownerOf.get(type)
}

/* ⚠️ 这个必须**每次现查**（不能像以前那样把 `registry.makeModelOfType`
 * 存下来）—— 装卸包之后注册表是新的，存下来的旧函数会一直用旧目录。 */
export function makeModelOfType(type: string, id: string) {
  const def = byType.get(type)
  if (!def) return null
  return {
    id,
    type: def.type,
    title: def.title,
    category: def.category,
    width: def.width,
    height: def.height,
    render: def.render,
    editorOnly: def.editorOnly,
    /* 深拷贝：模板共享，实例必须是自己的（见 `cloneRows`） */
    rows: JSON.parse(JSON.stringify(def.rows)) as NodeTypeDef['rows'],
  }
}

/**
 * 内置求值器 —— **全部包合起来**的那一份（原地更新的对象）。
 *
 * ⚠️ 名字没变，所以求值层的用法一行没改；变的是它的**来源**：
 * 以前是内核里一张写死的表，现在是「内核包 + 各数据包」合并的结果。
 */
export const BUILTIN_EVALUATORS: Record<string, NodeEvaluator> = evaluators

/**
 * **副作用执行器**（运行期用，见 `node/runGraph.ts`）。
 *
 * ⚠️ 和 `BUILTIN_EVALUATORS` 一样是**原地改写**的普通对象（装卸包时改它）——
 * 宿主读它的人要一起读 `packagesVersion`，否则"装了包但还是跑不了"。
 */
export const BUILTIN_RUNNERS: Record<string, NodeRunner> = runners

/**
 * 全部包合起来的**组节点资产**（「添加资产」菜单用它）。
 *
 * ⚠️ 和 `NODE_TYPES` 一样是**原地更新**的容器 —— 换数组对象的话，
 * 已经 import 走它的模块看不到装卸包之后的变化。
 */
export const ASSETS: GroupAssetDef[] = assets

/** 有问题的资产（结构不对 / 内部图引用了不存在的节点类型）—— 正常应当是空的 */
export const ASSET_PROBLEMS = assetProblems

/**
 * ★ **示例文档**（「文件 → 示例文件」那一栏的清单）。
 *
 * ⚠️ 和 `ASSETS` 一样是**原地更新**的容器：装卸包之后菜单要跟着变，
 * 换数组对象的话读它的地方看不到。
 */
export const EXAMPLES: PackageExample[] = examples

/** 按 id 取一份示例（菜单点击时用） */
export function exampleOf(id: string): PackageExample | undefined {
  return examples.find((item) => item.id === id)
}

export type { NodeTypeDef }
